import _isEqual from "lodash/isEqual";
import { useContext, useEffect, useRef } from 'react';
import AnalyticsContext from './AnalyticsContext';
/**
 * @callback UseWaitCallback
 * @returns {void}
 */

/**
 * Hook to perform an action after a value has remained unchanged for a
 * certain period of time. For instance, if `delay` is 200ms and `value`
 * remains the same for at least 200ms, `callback` will be invoked.
 *
 * @param {any[]} values - One or more values to "watch"
 * @param {number} delay - The delay in milliseconds
 * @param {UseWaitCallback} callback - Invoked when `value` remains unchanged for `delay` ms
 */

var useWait = function useWait(values, delay, callback) {
  useEffect(function () {
    var timeout = setTimeout(function () {
      return callback();
    }, delay);
    return function () {
      return clearTimeout(timeout);
    };
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
  values);
};
/**
 * Automatically fires an event when the given type and payload have remained
 * unchanged for the given debounce period. Events will be refired when any
 * part of the event changes.
 *
 * If you want to fire the same payload multiple times, you can use the
 * `additionalTrigger` option. This value won't be included in the event's
 * payload, but it will cause events to refire after the delay when it changes.
 * This value can be anything - a string, a number, an array, an object, etc.
 *
 * @param {Object} options
 * @param {string} options.eventType - The type of the event
 * @param {Object} options.payload - The payload of the event
 * @param {any} options.additionalTrigger - Can be used to make an event
 *                                          refire without including the value
 *                                          in the payload.
 * @param {number} options.delay - How long to wait when debouncing changes
 * @param {boolean} options.fireInitial - Whether to fire the initial event
 */


var useDebouncedEvent = function useDebouncedEvent(_ref) {
  var eventType = _ref.eventType,
      _ref$payload = _ref.payload,
      payload = _ref$payload === void 0 ? {} : _ref$payload,
      _ref$additionalTrigge = _ref.additionalTrigger,
      additionalTrigger = _ref$additionalTrigge === void 0 ? null : _ref$additionalTrigge,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? 1000 : _ref$delay,
      _ref$fireInitial = _ref.fireInitial,
      fireInitial = _ref$fireInitial === void 0 ? true : _ref$fireInitial;
  // Pull the analytics instance from Context.
  var analytics = useContext(AnalyticsContext); // Compute the payload ahead of time - this is what we'll use to decide if
  // we need to refire an event. If the payload is different from the
  // previously-fired event (possibly "null" if no event has been fired yet),
  // we need to fire a new event.

  var currentEvent = {
    eventType: eventType,
    payload: payload,
    additionalTrigger: additionalTrigger
  }; // We use `useRef` instead of `useState` because we don't need to rerender
  // after an event is fired, and also because React doesn't guarantee that a
  // component will always render with the latest state. If we used `useState`
  // instead, we could end up with duplicate events.
  //
  // If `fireInitial` is set to `false`, we'll initialize `previousEvent` with
  // the current event at the time this hook is initialized to ensure that the
  // event only fires after the event changes from its initial state.

  var previousEvent = useRef(fireInitial ? null : currentEvent); // The event is serialized with JSON.stringify so that `useEffect` can
  // correctly identify when it should rerun - if we passed the whole object,
  // it would only do a shallow comparison.

  useWait([JSON.stringify(currentEvent)], delay, function () {
    // Fire a new event only if the new event does not match the previously fired event.
    if (!_isEqual(currentEvent, previousEvent.current)) {
      analytics.track(currentEvent.eventType, currentEvent.payload);
      previousEvent.current = currentEvent;
    }
  });
};

export { useDebouncedEvent, useWait };